/**
 * Shared types and helpers for savings-vault readers.
 *
 * Mirrors [../../lst/readers/shared.ts](../../lst/readers/shared.ts)
 * so cross-provider tooling (orchestrator slicing, parser shape) stays
 * uniform.
 */

/** 10**18 — scaling factor for normalised exchange rates. */
export const ONE_E18 = 10n ** 18n

/** One multicall item — `{ address, name, params }` matched against
 *  a parallel ABI array, identical to every other vault provider. */
export interface SavingsCall {
  address: string
  name: string
  params: readonly unknown[]
}

/** What a reader produces after consuming its multicall slice. */
export interface SavingsOnChainState {
  /** Total underlying held, raw. */
  totalAssets: bigint
  /** Total shares minted, raw. */
  totalSupply: bigint
  /** 1e18-scaled exchange rate: 1 share → X underlying.
   *  Normalised regardless of share decimals. */
  exchangeRate: bigint

  // ---- Optional, reader-specific extras -------------------------------
  // Absent on the generic ERC-4626 reader; populated by readers whose
  // protocol exposes a richer redemption surface (Native wNLP today).

  /** Exit fee in basis points, when the protocol charges one for
   *  skipping the queue — deducted from the underlying paid out, not
   *  collected separately. `0` means a free instant exit. Surfaces as
   *  `SavingsVault.withdrawFeeBps` (see there for the full mechanics). */
  withdrawFeeBps?: number
  /** Whether the instant (fee-paying) exit is enabled at all — some
   *  assets are queue-only. */
  instantRedeemEnabled?: boolean
  /** Underlying redeemable **this block** through the instant leg, raw.
   *  Read from the protocol's live inventory (not `totalAssets`), so it
   *  is the real cap on an instant exit. Drives `liquidity` for
   *  `fee-or-queued` vaults. */
  instantRedeemCapacity?: bigint
  /** Queue waiting period in seconds, read on-chain — authoritative over
   *  the registry's pinned `withdrawalCooldownSeconds`. */
  withdrawalCooldownSeconds?: number
  /** The queue contract a delayed redemption is requested from. */
  withdrawQueue?: string
  /** Protocol-internal share token this wrapper holds, when the vault is
   *  a wrapper over a second, rebasing share (Native wNLP → NTLP). */
  wrappedShareToken?: string

  /** Underlying still depositable **this block**, raw. Populated only by
   *  readers whose protocol caps deposits by a figure no `maxDeposit()`
   *  reports — Yield Basis gates on `AMM.max_debt()/2 >= value` and has
   *  no 4626 surface at all. Absent ⇒ uncapped (the normal case);
   *  `0n` ⇒ the vault is full and a deposit would revert. See
   *  `SavingsVault.depositCapacity`. */
  depositCapacity?: bigint
  /**
   * Which of BitFi's two withdrawal routes are actually open, and whether the
   * contract is paused.
   *
   * Populated only by the `bitfi-bfbtc` reader. It exists because BitFi
   * switches a route off by making its FEE unpayable — a 100 % percentage fee
   * on the EVM leg, `type(uint128).max` fixed on the Bitcoin leg — and there
   * is no boolean on the contract that says so. `previewWithdraw` answers 0
   * rather than reverting, so nothing else on the row would show it either.
   *
   * A row with both routes shut is a position that cannot be left on that
   * chain, which is not something `withdrawalMode: 'request-based'` can say.
   */
  bitfiExit?: {
    /** `requestWithdraw` — pays the underlying on this chain. */
    evmExitEnabled: boolean
    /** `requestWithdrawNative` — pays BTC on the Bitcoin network. */
    nativeExitEnabled: boolean
    paused: boolean
  }

  /**
   * Supply APR in percent, when the reader can derive it from the SAME
   * on-chain state it already reads. Absent ⇒ the entry's `yieldFetcher`
   * supplies the rate, which is the normal case.
   *
   * Exists for Venus's Liquidity Hub, whose rate is the TVL-weighted blend of
   * its YieldGroups' `spotAPYBps()` — the weights being the very
   * `totalAssets()` reads this state is built from. Sourcing that through a
   * `YieldFetcher` would blend a rate read in one block against weights read
   * in another, for no gain: the Hub publishes no APY view by design and no
   * off-chain source carries one.
   */
  supplyRate?: number

  /**
   * Incentive APR the vault's POSITION earns and a depositor does not, in
   * percent. Absent ⇒ not established; `0` ⇒ established and currently none.
   *
   * Deliberately not `rewardsRate`: that one feeds
   * `depositRate = supplyRate + rewardsRate`, i.e. what a depositor actually
   * earns, and folding this into it would overstate the row.
   *
   * Venus's Liquidity Hub is the case. Its Core YieldGroup holds vTokens, so
   * XVS supply emissions accrue to that contract in the Comptroller — but no
   * function on the Hub, the YieldGroup or `AdapterCoreV1` claims them, and
   * nothing moves them into `totalAssets()`. Once someone calls the
   * permissionless `claimVenus`, the XVS lands on the YieldGroup and can only
   * leave through an ACM-gated `sweep`. So the value is real, it is measurable,
   * and it is not the depositor's.
   *
   * Every Core market the Hubs use read `venusSupplySpeeds == 0` at
   * integration, which is why publishing this matters more than it looks: the
   * gap opens silently the day governance turns emissions back on, and nothing
   * else on the row would move.
   */
  strandedRewardsRate?: number

  /**
   * Shares minted for ONE raw underlying unit, when the vault's ENTRY price is
   * not simply `1 / exchangeRate`.
   *
   * Absent ⇒ the row derives it from `exchangeRate`, which is right for every
   * vault that mints at par. Saturn's `sUSDat` charges `depositFeeBps` as a
   * separate transfer inside `_deposit` and mints on the remainder, so only
   * `previewDeposit` sees it — the dial is 0 today and admin-settable to 500,
   * which is exactly the situation where a derived figure goes quietly wrong.
   */
  convertToShares?: bigint
  /**
   * ENTRY fee in basis points, the mirror of `withdrawFeeBps`. Absent ⇒ not
   * read (NOT "free"); `0` ⇒ read, and currently free.
   *
   * Charged out of the deposit before shares are minted, so a depositor funds
   * `assets` and is credited on `assets × (1 − bps/10_000)`.
   */
  depositFeeBps?: number
  /**
   * Smallest amount the vault's exit accepts, RAW underlying. Surfaces as the
   * queued route's `minAmount`.
   *
   * Read live because it is what makes a single-route exit reachable or not:
   * Saturn's `requestRedeem` reverts under `MIN_WITHDRAWAL` (10 USDat) and
   * there is no second leg to fall back to.
   */
  exitMinAmount?: bigint

  /** 1e18-scaled **fundamental** underlying-per-share, when the protocol
   *  publishes a second, oracle-anchored per-share value distinct from
   *  the redeemable one that feeds `exchangeRate`. Yield Basis's
   *  `pricePerShare()` is this; the gap to `exchangeRate` is its TRD.
   *  Absent for every vault with a single price. */
  fundamentalExchangeRate?: bigint
}

/** A self-contained slice of the multicall payload plus a parser
 *  that consumes exactly `calls.length` results. */
export interface SavingsReader {
  calls: SavingsCall[]
  /** ABI per call slot. `any[]` to accept `as const` fragments without
   *  per-call casting — the downstream multicall API is `any`-typed. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  abis: any[]
  /** Returns `undefined` on parse failure so the orchestrator can
   *  skip the entry without poisoning the batch. */
  parse: (slice: unknown[]) => SavingsOnChainState | undefined
}

/**
 * Safely coerce a multicall return cell to `bigint`. Failures surface
 * as `undefined` so the parser can bail on the whole entry without
 * poisoning the batch.
 */
export const toBigInt = (v: unknown): bigint | undefined => {
  if (v === undefined || v === null) return undefined
  if (typeof v === 'bigint') return v
  if (typeof v === 'number') return BigInt(v)
  if (typeof v === 'string') {
    // Empty/short hex strings (`'0x'`) come back when a call reverts
    // with no return data or hits an unknown selector. `BigInt('0x')`
    // throws — return undefined so the parser drops the entry.
    if (v === '' || v === '0x' || v === '0X') return undefined
    try {
      return BigInt(v)
    } catch {
      return undefined
    }
  }
  return undefined
}
